import React, { useState, useEffect } from 'react';
import styles from './CommentSection.module.css';
import API_BASE_URL from '../config/api';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { Link } from 'react-router-dom';
import { buildStorageUrl } from '../config/firebaseStorage';
import ReportMessageDialog from './ReportMessageDialog';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import TranslateToggle from './TranslateToggle';

const CommentItem = ({ comment, allComments, addReply, onToggleLike, onReport, onDelete, currentUser, firebaseUser, canLike = false, depth = 0 }) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef(null);
  const { t } = useTranslation();

  const currentUserId = currentUser?.uid || '';
  const isAuthor = currentUserId === comment.userId;
  const isStaff = currentUser?.admin || currentUser?.editor;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const username = (comment?.username || '').trim();
  const profileUrl = username ? `/profile/${encodeURIComponent(username)}` : '';
  const avatarUrl = (comment?.userPfp || '').trim() || buildStorageUrl('pfp/placeholderP.png');
  
  // Find replies
  const replies = allComments.filter(c => c.parentId === comment.id);
  const [collapsed, setCollapsed] = useState(false);
  const isReported = comment.reported === true;
  const previewText = isReported ? "[Reported content]" : ((comment?.text || '').replace(/\s+/g, ' '));

  const likeCount = Object.keys(comment?.likes || {}).length;
  const isLiked = Boolean(currentUserId && comment?.likes?.[currentUserId]);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    await addReply(replyText, comment.id);
    setReplyText('');
    setShowReplyInput(false);
  };

  const stopProp = (e) => { e.stopPropagation(); };

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  return (
    <div className={styles.commentItem}>
      <div className={styles.commentCard}>
        <div className={styles.commentHeader}>
          {!(collapsed && replies.length > 0) && (
            <img 
              src={avatarUrl}
              alt={username ? `Avatar of ${username}` : 'User avatar'}
              className={styles.avatar} 
            />
          )}
          {collapsed && replies.length > 0 ? (
            <div className={styles.collapsedInline}>
              <span className={styles.collapsedUsername}>{username || 'User'}</span>
              <span className={styles.collapsedTextInline}>{(previewText.length > 120) ? (previewText.substring(0,116).trim() + '...') : previewText}</span>
            </div>
          ) : (
            <>
              {profileUrl ? (
                <Link to={profileUrl} className={styles.usernameLink} onClick={stopProp}>
                  <span className={styles.username}>{username}</span>
                </Link>
              ) : (
                <span className={styles.username}>{username || 'User'}</span>
              )}
              <span className={styles.timestamp}>{formatDate(comment.timestamp)}</span>
            </>
          )}

          {(firebaseUser) && (
            <div className={styles.menuContainer} ref={menuRef}>
              {replies.length > 0 && (
                <button
                  className={styles.collapseButton}
                  onClick={(e) => { stopProp(e); setCollapsed(!collapsed); }}
                  aria-pressed={collapsed}
                  title={collapsed ? 'Expand thread' : 'Collapse thread'}
                >
                  {collapsed ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg>
                  )}
                </button>
              )}
              <button 
                className={styles.menuButton} 
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Comment actions"
              >
                <svg style={{width:'1.25rem', height:'1.25rem'}} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                </svg>
              </button>
              {menuOpen && (
                <div className={styles.dropdown}>
                  {(!isAuthor || isStaff) && (
                    <button 
                      className={styles.dropdownItem} 
                      onClick={() => { onReport?.(comment); setMenuOpen(false); }}
                    >
                      Report
                    </button>
                  )}
                  {(isAuthor || isStaff) && (
                    <button 
                      className={`${styles.dropdownItem} ${styles.deleteItem}`} 
                      onClick={() => { onDelete?.(comment.id); setMenuOpen(false); }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {! (collapsed && replies.length > 0) && (
          <p className={styles.text}>
            {isReported ? (
              <span style={{ color: '#888', fontStyle: 'italic' }}>[Reported content]</span>
            ) : (
              comment.text
            )}
          </p>
        )}
        {!((collapsed && replies.length > 0)) && (
          <div onClick={stopProp}>
            <TranslateToggle text={comment.text || ''} />
          </div>
        )}
        {!((collapsed && replies.length > 0)) && (
          <div className={styles.actions}>
           <button className={styles.actionButton} onClick={() => setShowReplyInput(!showReplyInput)} aria-label={t('community.comments.reply') || 'Reply'}>
             <svg style={{width:'1.25rem', height:'1.25rem'}} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
             </svg>
           </button>

           <button
             className={`${styles.actionButton} ${isLiked ? styles.liked : ''}`}
             type="button"
             onClick={() => onToggleLike?.(comment)}
             disabled={!canLike}
             title={!canLike ? (t('community.comments.loginRequired') || 'Please login to comment') : ''}
             aria-label={isLiked ? (t('community.comments.unlike') || 'Unlike') : (t('community.comments.like') || 'Like')}
           >
             <span className={styles.heartIcon}>{isLiked ? '♥' : '♡'}</span>
             <span className={styles.countLabel}>{likeCount}</span>
            </button>
           </div>
          )}
        {showReplyInput && (
          <form className={`${styles.inputSection} ${styles.replyInput}`} onSubmit={handleSubmitReply}>
            <textarea
              className={styles.textarea}
              placeholder={t('community.comments.placeholder') || 'Write a reply...'}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={2}
            />
            <div className={styles.replyActions}>
              <button
                type="button"
                className={`${styles.submitButton} ${styles.cancelButton}`}
                onClick={() => setShowReplyInput(false)}
              >
                {t('action.cancel') || 'Cancel'}
              </button>
              <button type="submit" className={styles.submitButton}>
                {t('community.comments.post') || 'Reply'}
              </button>
            </div>
          </form>
        )}
      </div>
      
      {!collapsed && replies.length > 0 && (
        <div className={styles.replies}>
          {replies.map(reply => (
            <CommentItem 
              key={reply.id} 
              comment={reply} 
              allComments={allComments} 
              addReply={addReply}
              onToggleLike={onToggleLike}
              onReport={onReport}
              onDelete={onDelete}
              currentUser={currentUser}
              firebaseUser={firebaseUser}
              canLike={canLike}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { currentUser, firebaseUser } = useUser(); // Ensure we use auth context
  const { t } = useTranslation();
  const [reportDialog, setReportDialog] = useState({ open: false, comment: null });

  const readResponsePayload = async (res) => {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.toLowerCase().includes('application/json')) {
      return res.json();
    }
    const text = await res.text();
    return { success: false, message: text || `HTTP ${res.status}` };
  };

  const fetchComments = async () => {
    setLoading(true);
    setListError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/posts/${postId}/comments`);
      const data = await readResponsePayload(res);

      if (!res.ok || !data?.success) {
        const message = data?.message || t('community.comments.loadFailed') || 'Failed to load comments.';
        setListError(message);
        setComments([]);
        return;
      }

      setComments(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error(err);
      setListError(err?.message || t('community.comments.loadFailed') || 'Failed to load comments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const handleAddComment = async (text, parentId = null) => {
    setSubmitError('');
    if (!firebaseUser) {
        alert(t('community.comments.loginRequired') || 'Please login to comment');
        return;
    }
    
    try {
      setSubmitting(true);
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text, parentId })
      });

      const data = await readResponsePayload(res);
      if (!res.ok || !data?.success) {
        const message = data?.message || t('community.comments.postFailed') || 'Failed to post comment.';
        setSubmitError(message);
        return;
      }

      setComments(prev => [...prev, { ...data.data, timestamp: new Date() }]);
    } catch (err) {
      console.error(err);
      setSubmitError(err?.message || t('community.comments.postFailed') || 'Failed to post comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleLike = async (comment) => {
    if (!firebaseUser?.uid || !comment?.id) {
      alert(t('community.comments.loginRequired') || 'Please login to comment');
      return;
    }

    const userId = firebaseUser.uid;
    const alreadyLiked = Boolean(comment?.likes?.[userId]);

    setComments((prev) =>
      prev.map((item) => {
        if (item.id !== comment.id) return item;
        const nextLikes = { ...(item.likes || {}) };
        if (alreadyLiked) {
          delete nextLikes[userId];
        } else {
          nextLikes[userId] = new Date();
        }
        return { ...item, likes: nextLikes };
      })
    );

    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/comments/${comment.id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await readResponsePayload(res);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || t('community.comments.likeFailed') || 'Failed to like comment.');
      }
    } catch (err) {
      console.error(err);
      setSubmitError(err?.message || t('community.comments.likeFailed') || 'Failed to like comment.');
      await fetchComments();
    }
  };

  const handleReport = async (comment, { reason, details }) => {
    try {
      if (!firebaseUser?.uid) {
        alert(t('community.comments.loginRequired') || 'Please login to comment');
        return;
      }
      const submitReport = httpsCallable(functions, 'submitReport');
      await submitReport({
        type: 'comment',
        postId,
        commentId: comment?.id,
        commentText: comment?.text || '',
        commentUserId: comment?.userId || '',
        reason,
        details,
      });
      alert('Report submitted. Thank you for helping keep the community safe.');
    } catch (e) {
      console.error(e);
      alert('Failed to report: ' + (e?.message || 'Unknown error'));
    } finally {
      setReportDialog({ open: false, comment: null });
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!firebaseUser) return;
    if (!window.confirm(t('community.comments.confirmDelete') || 'Are you sure you want to delete this comment?')) return;

    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await readResponsePayload(res);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to delete comment');
      }

      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleSubmitMain = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    await handleAddComment(newComment);
    setNewComment('');
  };

  const rootComments = comments.filter(c => !c.parentId);

  return (
    <div className={styles.commentSection}>
      <h3 className={styles.title}>{t('community.comments.title') || 'Comments'}</h3>
      
      {firebaseUser ? (
        <form className={styles.inputSection} onSubmit={handleSubmitMain}>
          <textarea
            className={styles.textarea}
            placeholder={t('community.comments.placeholder') || 'What are your thoughts?'}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={submitting}
          />
          <button type="submit" className={styles.submitButton} disabled={submitting || !newComment.trim()}>
             {t('community.comments.post') || 'Post Comment'}
          </button>
          {submitError && (
            <p className={`${styles.error} ${styles.submitError}`}>{submitError}</p>
          )}
        </form>
      ) : (
        <p className={styles.hint}>
          {t('community.comments.loginToComment') || 'Log in to write a comment.'}
        </p>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className={styles.commentList}>
          {listError && (
            <p className={styles.error}>{listError}</p>
          )}
          {rootComments.length > 0 ? rootComments.map(comment => (
            <CommentItem 
              key={comment.id} 
              comment={comment} 
              allComments={comments} // Pass full list for recursion
              addReply={handleAddComment}
              onToggleLike={handleToggleLike}
              onReport={(c) => setReportDialog({ open: true, comment: c })}
              onDelete={handleDeleteComment}
              currentUser={currentUser}
              firebaseUser={firebaseUser}
              canLike={Boolean(firebaseUser?.uid)}
            />
          )) : (
            <p className={styles.empty}>No comments yet.</p>
          )}
        </div>
      )}

      <ReportMessageDialog
        open={reportDialog.open}
        messageText={reportDialog.comment?.text || ''}
        onClose={() => setReportDialog({ open: false, comment: null })}
        onSubmit={(data) => handleReport(reportDialog.comment, data)}
      />
    </div>
  );
};

export default CommentSection;
